import {
  MRT_GlobalFilterTextField,
  MaterialReactTable,
  type MRT_ColumnDef,
  useMaterialReactTable,
} from 'material-react-table';
import { Box, Button, lighten, Stack } from '@mui/material';
import { RemoveRedEye } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import CurrencyFormat from '@/utils/CurrencyFormat';

type StudentData = {
  student: {
    id: string;
    nome: string;
  };
  total_payments: number;
  total_payments_expiring: number;
  total_payments_expired: number;
  medium_days_expire: number;
  total_paid_time: number;
  total_paid_after: number;
  financial_score: number;
  total_discount: number;
};

const columns: Array<MRT_ColumnDef<StudentData>> = [
  {
    accessorKey: 'student.nome',
    header: 'Aluno',
    sortDescFirst: true,
    enableMultiSort: true,
    minSize: 100,
    maxSize: 150,
    Cell: ({ renderedCellValue }) => (
      <div style={{ marginLeft: '40px' }}>{renderedCellValue}</div>
    ),
  },
  {
    accessorKey: 'total_discount',
    header: '% desconto',
    minSize: 100,
    maxSize: 150,
    Cell: ({ renderedCellValue }) => (
      // <div style={{ marginLeft: '20px' }}>{}</div>
      <Box
      sx={{
        height: '60px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
     {renderedCellValue} %
    </Box>
    ),
  },
  {
    accessorKey: 'total_payments',
    header: 'Qtd. Boletos recebíveis',
    minSize: 100,
    maxSize: 150,
    Cell: ({ renderedCellValue }) => (
        <Box
      sx={{
        height: '60px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >{renderedCellValue}</Box>
    ),
  },
  {
    accessorKey: 'total_payments_expiring',
    header: 'Qtd. Boletos à vencer',
    minSize: 100,
    maxSize: 150,
    Cell: ({ renderedCellValue }) => (
        <Box
      sx={{
        height: '60px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >{renderedCellValue}</Box>
    ),
  },
  {
    accessorKey: 'total_payments_expired',
    header: 'Qtd. Boletos vencidos',
    minSize: 100,
    maxSize: 150,
    Cell: ({ renderedCellValue }) => (
      <Box
        sx={{
          // backgroundColor: 'rgba(252, 228, 212, 1)',
          height: '60px',
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
       {renderedCellValue}
      </Box>
    ),
  },
  {
    accessorKey: 'medium_days_expire',
    header: 'Média de dias de atraso',
    minSize: 100,
    maxSize: 150,
    Cell: ({ renderedCellValue }) => (
      <Box
        sx={{
          // backgroundColor: 'rgba(252, 228, 212, 1)',
          height: '60px',
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        {renderedCellValue}
      </Box>
    ),
  },
  {
    accessorKey: 'total_paid_time',
    header: 'Qtd. Boletos pagos em tempo',
    minSize: 100,
    maxSize: 150,
    Cell: ({ renderedCellValue }) => (
      <Box
        sx={{
          // backgroundColor: 'rgba(252, 228, 212, 1)',
          height: '60px',
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
       {renderedCellValue}
      </Box>
    ),
  },
  {
    accessorKey: 'financial_score',
    header: 'Score Financeiro',
    minSize: 100,
    maxSize: 150,
    Cell: ({ row, renderedCellValue }) => {
      const totalPayments = row.original.total_payments;
      const isZero = totalPayments === 0;
      return (
        <Box
          sx={{
            backgroundColor: isZero ? 'yellow' : '#CEFACE',
            height: '60px',
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {isZero ? 'Bolsista' : renderedCellValue}
        </Box>
      );
    },
  },
];

type TableHeaderCustomProps = {
  data: StudentData[];
  toBack: string;
};

const TableHeaderCustomTermometro = ({ data, toBack }: TableHeaderCustomProps) => {
  const navigate = useNavigate();
  const tableBasic = useMaterialReactTable({
    columns,
    data,
    positionActionsColumn: 'last',
    enableColumnFilterModes: false,
    enableColumnOrdering: false,
    enableGrouping: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableColumnFilters: false,
    enableHiding: false,
    enableExpandAll: false,
    enableRowDragging: false,
    enableColumnActions: false,
    enableRowActions: true,
    enableRowSelection: false,
    enableBottomToolbar: false,
    localization: {
      search: 'Pesquisar por nome do aluno',
      actions: 'Ações', 
    },
    initialState: {
      showGlobalFilter: true,
    },
    paginationDisplayMode: 'default',
    enablePagination: false,
    positionToolbarAlertBanner: 'bottom',
    muiSearchTextFieldProps: {
      variant: 'outlined',
      size: 'small',
      fullWidth: true,          
      placeholder: 'Pesquisar por nome…',
    },
    muiPaginationProps: {
      color: 'secondary',
      rowsPerPageOptions: [10, 20, 30],
      shape: 'rounded',
      variant: 'outlined',
    },
    renderRowActions: ({ row }) => {
      const handleClick = () => {
        const id = row.original.student.id;
        const name = row.original.student.nome;
        navigate(toBack, { state: { id, name } });
      };

      return (
        <Stack alignItems={'center'}>
          <RemoveRedEye
            sx={{
              cursor: 'pointer'
            }}
            onClick={handleClick} />
        </Stack>
      );
    },
    muiTableHeadCellProps: () => ({
      sx: {
        backgroundColor: 'rgb(247, 247, 247)',
        color: 'black',
        fontWeight: 400,
        fontSize: '12px',
        alignItems: 'center',
        '.Mui-TableHeadCell-Content': {
          justifyContent: 'center'
        }
      },
    }),
    muiTableBodyCellProps: () => ({
      sx: {
        backgroundColor: '#fff',
        color: 'rgba(128, 128, 128, 1)',
        fontWeight: 400,
        fontSize: '12px',
        paddingX: '20px',
        padding: '0',
      },
    }),
    renderTopToolbar: ({ table }) => (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        p={1}
      >
        {/* LEFT‑aligned, full width filter */}
        <Box flexGrow={6} mr={2}>
          <MRT_GlobalFilterTextField
            table={table}
            //@ts-ignore
            textFieldProps={{
              placeholder: 'Pesquisar por nome do aluno…',
              variant: 'outlined',
              size: 'small',
              fullWidth: true,
            }}
          />
        </Box>

        {/* if you have any right‑side buttons, put them here */}
        {/* <Button onClick={…}>My Action</Button> */}
      </Box>
    )
  });

  return (
    <>
      <MaterialReactTable table={tableBasic} />
    </>
  );
};

export default TableHeaderCustomTermometro;
